import { useEffect, useState, type ReactNode } from 'react';
import { cx } from '../cx';

export type TocItem = { id: string; label: ReactNode; /** 2 for sections, 3 for sub-sections. */ level?: 2 | 3 };

export type TableOfContentsProps = {
  items: TocItem[];
  /** Heading above the list ("On this page"). */
  title: ReactNode;
  /** Accessible name of the navigation. */
  label: string;
  /** The page's one action under the list (an alert prompt), optional. */
  action?: ReactNode;
  /** Stick under the navbar while the page scrolls (from lg). */
  sticky?: boolean;
  className?: string;
};

/** Which section is current: the last heading whose top has passed the reading line. Pure, exported for tests. */
export function currentSection(tops: Array<{ id: string; top: number }>, readingLine = 120): string | null {
  let current: string | null = tops[0]?.id ?? null;
  for (const t of tops) if (t.top <= readingLine) current = t.id;
  return current;
}

/**
 * The contents of a long page (articles, legal pages, how-to guides): links to each section, the
 * one being read marked with aria-current="location".
 */
export function TableOfContents({ items, title, label, action, sticky = true, className }: TableOfContentsProps) {
  const [active, setActive] = useState<string | null>(items[0]?.id ?? null);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const tops = items
          .map((i) => ({ id: i.id, el: document.getElementById(i.id) }))
          .filter((x): x is { id: string; el: HTMLElement } => !!x.el)
          .map((x) => ({ id: x.id, top: x.el.getBoundingClientRect().top }));
        if (tops.length) setActive(currentSection(tops));
      });
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', update);
    };
  }, [items]);

  return (
    <nav aria-label={label} className={cx('flex flex-col gap-3', sticky && 'lg:sticky lg:top-24', className)}>
      <span className="type-eyebrow text-jc-ink-secondary">{title}</span>
      <ol className="flex flex-col border-l-2 border-jc-border">
        {items.map((item) => {
          const current = item.id === active;
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                aria-current={current ? 'location' : undefined}
                className={cx(
                  '-ml-0.5 block border-l-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus',
                  item.level === 3 ? 'pl-6' : 'pl-3.5',
                  current
                    ? 'border-jc-ink font-semibold text-jc-ink'
                    : 'border-transparent text-jc-ink-secondary hover:text-jc-ink',
                )}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ol>
      {action ? <div className="pt-2">{action}</div> : null}
    </nav>
  );
}
